import React, { useState, useMemo } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../../components/Providers/FloatMessage';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    ReorderPlaylist,
    SetNowPlaying,
    TogglePlayed,
    ToggleFavorite,
    CopyTrackToClipboard,
    OpenSpotifySearch,
    OpenYouTubeSearch
} from '../../../../utilities/BL/Admin/Overlays/Playlist';

/**
 * Panel Derecho: Playlist activa de estadio con reordenamiento interactivo (Drag & Drop y botones ▲/▼),
 * herramientas DJ y controles de estado en vivo (Now Playing, IsPlayed, Remover).
 * 
 * @component PlaylistActiveList
 * @param {Object} props
 * @param {Array} props.activePlaylist - Lista de canciones actualmente en la playlist activa.
 * @param {string|null} props.currentPlayingSongId - ID de la pista actual en reproducción.
 * @param {Function} props.onRefresh - Callback para recargar los datos.
 * @param {Function} props.onPlaylistChange - Callback optimista para actualizar estado local de playlist.
 */
export const PlaylistActiveList = ({
    activePlaylist = [],
    currentPlayingSongId = null,
    onRefresh,
    onPlaylistChange
}) => {
    const { showMessage } = useFloatMessage();

    // Filtro de búsqueda local
    const [searchTerm, setSearchTerm] = useState('');

    // Estados Drag & Drop HTML5
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [dragOverIndex, setDragOverIndex] = useState(null);

    // Modal Confirm
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        actionToYesButton: null,
        textYesButton: 'Sí, remover',
        textNoButton: 'Cancelar'
    });

    // Copiar pista al portapapeles
    const handleCopy = async (title, artist) => {
        const res = await CopyTrackToClipboard(title, artist);
        if (res.success) {
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: res.message,
                message: res.message
            });
        } else {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: res.message,
                message: res.message
            });
        }
    };

    // Alternar Ahora Reproduciendo (Now Playing)
    const handleSetNowPlaying = async (song) => {
        const isCurrent = currentPlayingSongId === song.id || song.isNowPlaying;
        const targetSongId = isCurrent ? null : song.id;

        try {
            const res = await SetNowPlaying(targetSongId);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: targetSongId
                        ? `▶️ Reproduciendo ahora: "${song.title}"`
                        : 'Pista en reproducción desmarcada.',
                    message: targetSongId
                        ? `▶️ Reproduciendo ahora: "${song.title}"`
                        : 'Pista en reproducción desmarcada.'
                });
                if (onRefresh) onRefresh();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al cambiar pista en reproducción.',
                    message: (res && res.message) || 'Error al cambiar pista en reproducción.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al marcar pista en reproducción.',
                message: 'Error de conexión al marcar pista en reproducción.'
            });
        }
    };

    // Alternar Reproducida (IsPlayed)
    const handleTogglePlayed = async (song) => {
        try {
            const nextState = !song.isPlayed;
            const res = await TogglePlayed(song.id, nextState);
            if (res && res.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: nextState
                        ? `✅ "${song.title}" marcada como reproducida.`
                        : `"${song.title}" marcada como pendiente.`,
                    message: nextState
                        ? `✅ "${song.title}" marcada como reproducida.`
                        : `"${song.title}" marcada como pendiente.`
                });
                if (onRefresh) onRefresh();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: (res && res.message) || 'Error al actualizar estado reproducido.',
                    message: (res && res.message) || 'Error al actualizar estado reproducido.'
                });
            }
        } catch (err) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de red al alternar estado reproducido.',
                message: 'Error de red al alternar estado reproducido.'
            });
        }
    };

    // Quitar de Playlist Activa con Confirmación
    const handleRemoveFromActive = (song) => {
        setConfirmConfig({
            message: `¿Deseas remover "${song.title}" de la playlist activa del estadio?`,
            textYesButton: 'Remover',
            textNoButton: 'Cancelar',
            actionToYesButton: async () => {
                const res = await ToggleFavorite(song.id, false);
                if (res && res.success) {
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `"${song.title}" removida de la playlist activa.`,
                        message: `"${song.title}" removida de la playlist activa.`
                    });
                    if (onRefresh) onRefresh();
                    return { success: true };
                }
                return { success: false, message: (res && res.message) || 'Error al remover pista de la playlist.' };
            }
        });
        setShowConfirm(true);
    };

    // Mover Arriba / Abajo mediante botones ▲/▼
    const handleMovePosition = async (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= activePlaylist.length) return;

        const updated = [...activePlaylist];
        const [movedItem] = updated.splice(index, 1);
        updated.splice(targetIndex, 0, movedItem);

        if (onPlaylistChange) {
            onPlaylistChange(updated);
        }

        const orderedIds = updated.map(s => s.id);
        const res = await ReorderPlaylist(orderedIds);
        if (!res || !res.success) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: (res && res.message) || 'Error al reordenar la playlist en el servidor.',
                message: (res && res.message) || 'Error al reordenar la playlist en el servidor.'
            });
            if (onRefresh) onRefresh();
        }
    };

    // =========================================================================
    // HTML5 Drag and Drop Handlers
    // =========================================================================
    const handleDragStart = (e, index) => {
        setDraggedIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', index.toString());
    };

    const handleDragOver = (e, index) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dragOverIndex !== index) {
            setDragOverIndex(index);
        }
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e, dropTargetIndex) => {
        e.preventDefault();
        if (draggedIndex === null || draggedIndex === dropTargetIndex) {
            setDraggedIndex(null);
            setDragOverIndex(null);
            return;
        }

        const updated = [...activePlaylist];
        const [draggedItem] = updated.splice(draggedIndex, 1);
        updated.splice(dropTargetIndex, 0, draggedItem);

        setDraggedIndex(null);
        setDragOverIndex(null);

        if (onPlaylistChange) {
            onPlaylistChange(updated);
        }

        const orderedIds = updated.map(s => s.id);
        const res = await ReorderPlaylist(orderedIds);
        if (!res || !res.success) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: (res && res.message) || 'Error al guardar el nuevo orden de la playlist.',
                message: (res && res.message) || 'Error al guardar el nuevo orden de la playlist.'
            });
            if (onRefresh) onRefresh();
        } else {
            showMessage({
                type: MESSAGE_TYPE.SUCCESS,
                text: 'Orden de playlist actualizado.',
                message: 'Orden de playlist actualizado.'
            });
        }
    };

    const handleDragEnd = () => {
        setDraggedIndex(null);
        setDragOverIndex(null);
    };

    // Filtro en vivo sobre la lista activa
    const filteredActiveList = useMemo(() => {
        if (!searchTerm.trim()) return activePlaylist;
        const query = searchTerm.toLowerCase().trim();
        return activePlaylist.filter(s =>
            (s.title && s.title.toLowerCase().includes(query)) ||
            (s.artistName && s.artistName.toLowerCase().includes(query)) ||
            (s.fanName && s.fanName.toLowerCase().includes(query))
        );
    }, [activePlaylist, searchTerm]);

    return (
        <section className="PlaylistActiveList">
            {/* Header del Panel */}
            <div className="PlaylistActiveList__Header">
                <div className="d-flex align-items-center gap-2">
                    <span className="PlaylistActiveList__Icon">
                        <i className="bi bi-disc-fill"></i>
                    </span>
                    <div>
                        <h3 className="PlaylistActiveList__Title mb-0">
                            Playlist Estadio
                            <span className="PlaylistActiveList__BadgeCount ms-2">
                                {activePlaylist.length}
                            </span>
                        </h3>
                        <p className="PlaylistActiveList__Subtitle mb-0">
                            Cola de reproducción en orden secuencial
                        </p>
                    </div>
                </div>

                <span className="PlaylistActiveList__LiveBadge">
                    <i className="bi bi-music-note-list me-1"></i> En Cabina
                </span>
            </div>

            {/* Buscador en vivo en la playlist activa */}
            <div className="PlaylistActiveList__Controls">
                <div className="PlaylistActiveList__SearchWrapper">
                    <i className="bi bi-search PlaylistActiveList__SearchIcon"></i>
                    <input
                        type="text"
                        className="PlaylistActiveList__SearchInput"
                        placeholder="Buscar en la playlist activa..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button
                            type="button"
                            className="PlaylistActiveList__SearchClear"
                            onClick={() => setSearchTerm('')}
                            title="Limpiar búsqueda"
                        >
                            <i className="bi bi-x"></i>
                        </button>
                    )}
                </div>

                <span className="PlaylistActiveList__DragHint">
                    <i className="bi bi-arrows-move me-1"></i> Arrastra para reordenar
                </span>
            </div>

            {/* Listado Activo Reordenable */}
            <div className="PlaylistActiveList__List">
                {activePlaylist.length === 0 ? (
                    <div className="PlaylistActiveList__EmptyState">
                        <i className="bi bi-star display-4 d-block mb-2 text-warning opacity-75"></i>
                        <p className="mb-1 fw-bold">No has añadido canciones a la playlist activa.</p>
                        <span className="small text-white-50">
                            Haz clic en la estrella ⭐ en el buzón de sugerencias (panel izquierdo) para sumar temas a la cabina.
                        </span>
                    </div>
                ) : filteredActiveList.length === 0 ? (
                    <div className="PlaylistActiveList__EmptyState">
                        <i className="bi bi-search display-6 d-block mb-2 text-white-50"></i>
                        <p className="mb-0">No se encontraron canciones en la playlist activa con "{searchTerm}".</p>
                    </div>
                ) : (
                    filteredActiveList.map((song, idx) => {
                        const originalIndex = activePlaylist.findIndex(s => s.id === song.id);
                        const isNowPlaying = (currentPlayingSongId === song.id) || song.isNowPlaying;
                        const isPlayed = song.isPlayed;
                        const isBeingDragged = draggedIndex === originalIndex;
                        const isDragOver = dragOverIndex === originalIndex;

                        return (
                            <div
                                key={song.id}
                                draggable={!searchTerm.trim()}
                                onDragStart={(e) => handleDragStart(e, originalIndex)}
                                onDragOver={(e) => handleDragOver(e, originalIndex)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, originalIndex)}
                                onDragEnd={handleDragEnd}
                                className={`PlaylistActiveList__Item ${
                                    isNowPlaying
                                        ? 'PlaylistActiveList__Item--nowPlaying'
                                        : isPlayed
                                        ? 'PlaylistActiveList__Item--played'
                                        : ''
                                } ${isBeingDragged ? 'PlaylistActiveList__Item--dragging' : ''} ${
                                    isDragOver ? 'PlaylistActiveList__Item--dragOver' : ''
                                }`}
                            >
                                {/* Handle Drag & Posición */}
                                <div className="PlaylistActiveList__PositionBox">
                                    <span
                                        className="PlaylistActiveList__DragHandle"
                                        title="Arrastra para reordenar"
                                    >
                                        <i className="bi bi-grip-vertical"></i>
                                    </span>
                                    <span className="PlaylistActiveList__Index">
                                        #{originalIndex + 1}
                                    </span>
                                </div>

                                {/* Información de la pista */}
                                <div className="PlaylistActiveList__Info">
                                    <div className="PlaylistActiveList__TitleRow">
                                        <span className="PlaylistActiveList__ItemTitle" title={song.title}>
                                            {song.title}
                                        </span>

                                        {/* Badge EN VIVO */}
                                        {isNowPlaying && (
                                            <span className="PlaylistActiveList__Badge PlaylistActiveList__Badge--live">
                                                <span className="PlaylistActiveList__LiveDot"></span>
                                                EN VIVO
                                            </span>
                                        )}

                                        {/* Badge Reproducida */}
                                        {isPlayed && (
                                            <span className="PlaylistActiveList__Badge PlaylistActiveList__Badge--played">
                                                <i className="bi bi-check2-circle me-1"></i> Tocada
                                            </span>
                                        )}
                                    </div>

                                    <div className="PlaylistActiveList__MetaRow">
                                        {song.artistName && (
                                            <span className="PlaylistActiveList__Artist">
                                                <strong>{song.artistName}</strong>
                                            </span>
                                        )}

                                        {song.voteCount > 0 && (
                                            <span className="PlaylistActiveList__Votes">
                                                🔥 {song.voteCount} votos
                                            </span>
                                        )}

                                        {song.fanName && (
                                            <span className="PlaylistActiveList__Fan">
                                                • {song.fanName}
                                            </span>
                                        )}
                                    </div>
                                </div>

                                {/* DJ Tools & Controles */}
                                <div className="PlaylistActiveList__Actions">
                                    {/* Botones de Reordenamiento ▲/▼ */}
                                    <div className="PlaylistActiveList__ReorderButtons">
                                        <button
                                            type="button"
                                            className="PlaylistActiveList__ArrowBtn"
                                            disabled={originalIndex === 0 || !!searchTerm.trim()}
                                            onClick={() => handleMovePosition(originalIndex, -1)}
                                            title="Subir posición"
                                        >
                                            <i className="bi bi-chevron-up"></i>
                                        </button>
                                        <button
                                            type="button"
                                            className="PlaylistActiveList__ArrowBtn"
                                            disabled={originalIndex === activePlaylist.length - 1 || !!searchTerm.trim()}
                                            onClick={() => handleMovePosition(originalIndex, 1)}
                                            title="Bajar posición"
                                        >
                                            <i className="bi bi-chevron-down"></i>
                                        </button>
                                    </div>

                                    {/* DJ Quick Tools */}
                                    <button
                                        type="button"
                                        className="PlaylistActiveList__ActionBtn"
                                        title="Copiar texto para software DJ"
                                        onClick={() => handleCopy(song.title, song.artistName)}
                                    >
                                        <i className="bi bi-clipboard"></i>
                                    </button>

                                    <button
                                        type="button"
                                        className="PlaylistActiveList__ActionBtn PlaylistActiveList__ActionBtn--spotify"
                                        title="Abrir en Spotify"
                                        onClick={() => OpenSpotifySearch(song.title, song.artistName)}
                                    >
                                        <i className="bi bi-spotify"></i>
                                    </button>

                                    <button
                                        type="button"
                                        className="PlaylistActiveList__ActionBtn PlaylistActiveList__ActionBtn--youtube"
                                        title="Abrir en YouTube"
                                        onClick={() => OpenYouTubeSearch(song.title, song.artistName)}
                                    >
                                        <i className="bi bi-youtube"></i>
                                    </button>

                                    {/* Botón Ahora Reproduciendo ▶️ */}
                                    <button
                                        type="button"
                                        className={`PlaylistActiveList__NowPlayingBtn ${
                                            isNowPlaying ? 'PlaylistActiveList__NowPlayingBtn--active' : ''
                                        }`}
                                        title={isNowPlaying ? 'Desmarcar Ahora Reproduciendo' : '▶️ Marcar como Ahora Reproduciendo'}
                                        onClick={() => handleSetNowPlaying(song)}
                                    >
                                        <i className={`bi ${isNowPlaying ? 'bi-stop-fill' : 'bi-play-fill'} me-1`}></i>
                                        {isNowPlaying ? 'Sonando' : 'Sonar'}
                                    </button>

                                    {/* Botón Marcada como Reproducida ✅ */}
                                    <button
                                        type="button"
                                        className={`PlaylistActiveList__ActionBtn ${
                                            isPlayed
                                                ? 'PlaylistActiveList__ActionBtn--playedActive'
                                                : 'PlaylistActiveList__ActionBtn--played'
                                        }`}
                                        title={isPlayed ? 'Marcar como no tocada' : 'Marcar como reproducida'}
                                        onClick={() => handleTogglePlayed(song)}
                                    >
                                        <i className="bi bi-check2-circle"></i>
                                    </button>

                                    {/* Botón Eliminar / Quitar de Playlist 🗑️ */}
                                    <button
                                        type="button"
                                        className="PlaylistActiveList__ActionBtn PlaylistActiveList__ActionBtn--delete"
                                        title="Quitar de playlist activa"
                                        onClick={() => handleRemoveFromActive(song)}
                                    >
                                        <i className="bi bi-trash"></i>
                                    </button>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            {/* Modal Confirmación Genérico */}
            {showConfirm && (
                <Confirm
                    message={confirmConfig.message}
                    textYesButton={confirmConfig.textYesButton}
                    textNoButton={confirmConfig.textNoButton}
                    actionToYesButton={confirmConfig.actionToYesButton}
                    setShowModal={setShowConfirm}
                />
            )}
        </section>
    );
};

export default PlaylistActiveList;
